import { onUnmounted, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'

/**
 * Composable to track user inactivity and trigger automatic logout.
 * 
 * @param timeoutInMinutes - Duration of inactivity before logout (default: 15 minutes)
 */
export function useIdleTimer(timeoutInMinutes: number = 15, warningInMinutes: number = 1) {
    const authStore = useAuthStore()
    const timer = ref<ReturnType<typeof setTimeout> | null>(null)
    const countdownInterval = ref<ReturnType<typeof setInterval> | null>(null)

    const timeoutMs = timeoutInMinutes * 60 * 1000
    const warningMs = warningInMinutes * 60 * 1000
    const idleThresholdMs = timeoutMs - warningMs

    const isActive = ref(false)
    const isWarningActive = ref(false)
    const remainingSeconds = ref(0)

    const resetTimer = () => {
        if (!isActive.value) return

        isWarningActive.value = false
        remainingSeconds.value = 0
        if (countdownInterval.value) {
            clearInterval(countdownInterval.value)
            countdownInterval.value = null
        }

        if (timer.value) clearTimeout(timer.value)

        // Timer for the warning threshold
        timer.value = setTimeout(() => {
            startWarning()
        }, idleThresholdMs)
    }

    const startWarning = () => {
        isWarningActive.value = true
        remainingSeconds.value = warningInMinutes * 60

        countdownInterval.value = setInterval(() => {
            remainingSeconds.value -= 1
            if (remainingSeconds.value <= 0) {
                if (countdownInterval.value) clearInterval(countdownInterval.value)
                logoutUser()
            }
        }, 1000)
    }

    const logoutUser = () => {
        if (authStore.isAuthenticated) {
            console.log(`[IdleTimer] User idle for ${timeoutInMinutes} minutes, triggering logout...`)
            authStore.logout()
        }
    }

    const events = [
        'mousedown',
        'mousemove',
        'keypress',
        'scroll',
        'touchstart',
        'click',
        'wheel',
        'keydown'
    ]

    const handleActivity = () => {
        if (isWarningActive.value) return // Don't reset automatically if warning is showing (manual reset required)
        resetTimer()
    }

    const start = () => {
        if (isActive.value) return
        isActive.value = true
        console.log(`[IdleTimer] Starting monitoring (${timeoutInMinutes}m total, ${warningInMinutes}m warning)`)

        events.forEach((event) => {
            window.addEventListener(event, handleActivity, { passive: true })
        })
        resetTimer()
    }

    const stop = () => {
        if (!isActive.value) return
        isActive.value = false
        isWarningActive.value = false
        console.log('[IdleTimer] Stopping monitoring')

        if (timer.value) {
            clearTimeout(timer.value)
            timer.value = null
        }
        if (countdownInterval.value) {
            clearInterval(countdownInterval.value)
            countdownInterval.value = null
        }
        events.forEach((event) => {
            window.removeEventListener(event, handleActivity)
        })
    }

    // Cleanup on unmount to prevent memory leaks
    onUnmounted(() => {
        stop()
    })

    return {
        start,
        stop,
        resetTimer,
        isActive,
        isWarningActive,
        remainingSeconds
    }
}
